<template>
  <form class="team-createform" @submit.prevent="onSubmit">
    <div class="line_flex">
      <input-text
        v-model:value="data.name"
        :label="$t('labels.teamName')"
        :autofocus="submitButtonVisible"
        :required="true"
        :error="state.fieldErrors.name"
      />
    </div>

    <p>
      {{ $t("texts.teamMemberInvitationMessage") }}
    </p>
    <input-email-list
      class="mt-4"
      :label="$t('labels.addMember')"
      :emails="memberEmails"
      :autocomplete-options="
        organizationMembers.map((m) => ({
          value: m.email || '',
          label: `${m?.name || ''}`
        }))
      "
      @add="addMember($event)"
    />
    <users-picker
      :users="organizationMembers"
      :selected-user-ids="selectedUserIds"
      @check-user="addMember($event.email)"
      @uncheck-user="removeMemberByEmail($event.email)"
    />

    <h4 class="label">{{ $t("headings.members") }}</h4>
    <table class="table-grid team-createform__members">
      <tbody>
        <tr
          v-for="member in data.members"
          :key="member.email"
          class="team-createform__member"
        >
          <td class="pv-3">{{ member.email }}</td>
          <td class="pv-3">{{ member.name }}</td>
          <td>
            <input-dropdown
              :value="member.roleCode"
              :options="['MEMBER', 'ADMIN']"
              @update:value="changeMemberRole(member, $event)"
            >
              <template #default="{ item }">
                {{ $t(`roleCodes.${item}`) }}
              </template>
            </input-dropdown>
          </td>
          <td>
            <button
              type="button"
              class="button-icon"
              @click="removeMember(member)"
            >
              <lucide-icon name="x" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-if="data.members.length === 0" class="message-info mt-0">
      {{ $t("messages.noTeamMembers") }}
    </p>

    <div v-if="state.error" class="message-error mt-4">
      {{ state.error.message }}
    </div>

    <div class="mt-4" v-if="submitButtonVisible">
      <button class="button" type="submit">
        {{ $t("actions.createTeam") }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { reactive, computed } from "vue";

import LucideIcon from "@core/ui/LucideIcon.vue";

import InputText from "@core/input/InputText.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import InputEmailList from "@core/input/InputEmailList.vue";

import UsersPicker from "@manager/common/UsersPicker.vue";

import { useUser } from "@/api/user.store";
import { useOrganizations } from "@manager/organizations/organizations.store";

import type { OrganizationMember } from "@/api/Organization";
import { BadRequestError } from "@core/errors/Errors";
import { showError } from "@core/ui/alert/alert";

const props = withDefaults(defineProps<{
  organizationSlug: string;
  organizationMembers?: OrganizationMember[];
  submitButtonVisible?: boolean;
}>(), {
  organizationMembers: () => [],
  submitButtonVisible: true
});

const emit = defineEmits<{
  (event: "create", team: any): void;
}>();

const { user } = useUser();
const { ROLE_CODES, createTeam, currentOrganization } = useOrganizations(
  props,
  { user }
);

const organization = currentOrganization;

type Member = {
  roleCode: string;
  email: string;
  name: string;
};

const data = reactive({
  members: [{
    roleCode: ROLE_CODES.ADMIN,
    email: user.value?.email || "",
    name: user.value?.name || ""
  }] as Member[],
  name: "",
  admin: null,
});
const state = reactive({
  fieldErrors: {} as Record<string, string>,
  error: null as null | Error
});

const memberEmails = computed(() => {
  return data.members.map((m) => m.email);
});

const selectedUserIds = computed(() => {
  return props.organizationMembers
    .filter((m) => memberEmails.value.includes(m.email || ""))
    .map((m) => m.id);
});

function addMember(email: string) {
  data.members.push({
    email: email,
    roleCode: ROLE_CODES.MEMBER,
    name: ""
  });
}

function changeMemberRole(member: Member, roleCode: string) {
  member.roleCode = roleCode;
}

function removeMemberByEmail(email: string) {
  const member = data.members.find((m) => m.email === email);
  if (member) {
    removeMember(member);
  }
}

function removeMember(member: Member) {
  // remove the member
  const index = data.members.findIndex((m) => m.email === member.email);
  if (index > -1) {
    data.members.splice(index, 1);
  }
}

async function tryCreatingTeam(teamData: {
  name: string;
  members: Member[];
}) {
  console.log("create team", teamData);
  if (!organization.value) {
    showError("No organization.");
    return;
  }
  try {
    return await createTeam(organization.value, teamData);
  } catch (e) {
    const error = e as BadRequestError;
    state.fieldErrors = error?.fields ?? {};
    state.error = error as Error;
  }
}

async function onSubmit() {
  try {
    const newTeam = await tryCreatingTeam({
      members: data.members,
      name: data.name
    });
    if (newTeam) {
      emit("create", newTeam);
      return true;
    }
  } catch (e) {
    showError(e as Error);
  }
  return false;
}

defineExpose({
  submit: onSubmit,
  data
});
</script>

<style lang="scss">
.team-createform {
  &__members {
    --gap: 5px;
    width: 100%;
  }
  &__member {
    display: grid;
    grid-template-columns: 1fr 1fr 12rem 3rem;
    align-items: center;
    border-radius: 5px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    padding-left: 1rem;
    background-color: var(--color-background-close);
  }
}
</style>
